import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { interactiveCellProps } from '@/components/data-table/interactive-cell';
import { openInNewTab } from '@/lib/open-in-new-tab';
import { cn } from '@/lib/utils';

const ICON_SIZE = {
  xs: 'size-3',
  sm: 'size-3.5',
  default: 'size-4',
} as const;

type BaseProps = Omit<ComponentProps<'a'>, 'target' | 'rel' | 'children' | 'aria-label'> & {
  href: string;
  /** Icon size: xs 12px (beside small text), sm 14px (table cells, headers; the default), default 16px. */
  size?: keyof typeof ICON_SIZE;
  /** In a DataTable cell with a row click: the click opens only the link, not the row too. */
  inCell?: boolean;
};

export type ExternalLinkProps = BaseProps &
  (
    | {
        /** The link's text. The arrow icon follows it, and "(opens in a new tab)" is read out. */
        children: ReactNode;
        /** `link` (primary, underline on hover; the default), `underline` (always underlined, in prose and
         * dialogs), `muted` (muted text that darkens on hover) or `inherit` (the text's own colour). */
        variant?: 'link' | 'underline' | 'muted' | 'inherit';
        /** Hide the arrow icon (a card or thumbnail that is the link itself). */
        icon?: boolean;
        /**
         * Medium weight: the link is a row's name or caption, or the one action on its line or under
         * an alert ("Open in GitHub"). Size comes from the container: put `text-xs` on the line.
         */
        strong?: boolean;
        label?: string;
      }
    | {
        /** Icon only: the muted arrow, as beside a name. `label` names it ("Open SF-T-42 in GitHub"). */
        children?: undefined;
        label: string;
        variant?: undefined;
        icon?: undefined;
        strong?: undefined;
      }
  );

/**
 * A link that opens in a new tab: somewhere outside the factory (GitHub, a tracker, a file),
 * or a factory page to keep beside this one. `rel="noopener noreferrer"`, and the click opens the tab
 * itself where an embedded browser panel would ignore target="_blank". With text it reads "Open in
 * GitHub ↗"; without, it is the muted arrow beside a name, named by `label`, also its hover hint.
 * The text's size comes from its container (a `text-xs` line, a table cell); `className` only
 * places it.
 */
export function ExternalLink({
  href,
  size = 'sm',
  inCell = false,
  children,
  label,
  variant = 'link',
  icon = true,
  strong = false,
  className,
  onClick,
  ...props
}: ExternalLinkProps) {
  // Some embedded browser panels ignore target="_blank", so the click opens the tab itself.
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) openInNewTab(event);
  };
  const cellProps = inCell ? interactiveCellProps<HTMLAnchorElement>(handleClick) : { onClick: handleClick };
  const iconNode = <ExternalLinkIcon className={cn('shrink-0', ICON_SIZE[size])} aria-hidden />;

  if (children == null) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-hint={label}
        className={cn(
          'inline-flex shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring',
          className
        )}
        {...props}
        {...cellProps}
      >
        {iconNode}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={cn(
        'inline-flex min-w-0 items-center gap-1 rounded-sm underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring',
        variant === 'link' && 'text-primary hover:underline',
        variant === 'underline' && 'underline',
        variant === 'muted' && 'text-muted-foreground hover:text-foreground hover:underline',
        variant === 'inherit' && 'hover:underline',
        strong && 'font-medium',
        className
      )}
      {...props}
      {...cellProps}
    >
      {children}
      {icon ? iconNode : null}
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}
